import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Slot, SlotOccurrence } from '@radioking/planning';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AddedSlotApi,
  convertOccurrenceToPlanningDatesApi,
  convertSlotToPlanningDatesApi,
  convertToAddedSlotApi,
  convertToDroppedSlotApi,
  convertToSlot,
  PlanningApi,
} from '../models/planning-api.model';

const planningUrls = {
  getPlanning: (id: number, from: string, to: string) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned/${from}/to/${to}`,
  addToPlanning: (id: number) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned`,
  editSlot: (id: number, slotId: number) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned/${slotId}`,
  moveOccurrence: (id: number, slotId: number, occurrenceId: number) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned/${slotId}/occurrence/${occurrenceId}`,
  deleteSlotFromPlanning: (id: number, slotId: number) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned/${slotId}`,
  deleteOccurrenceFromPlanning: (id: number, slotId: number, occurrenceId: number) =>
    `${environment.urls.OLD_MAIN_API}/radio/${id}/schedule/planned/${slotId}/occurrence/${occurrenceId}`,
};
@Injectable({
  providedIn: 'root',
})
export class PlanningService {
  constructor(private readonly httpClient: HttpClient) {}

  getPlanning(
    radioId: number,
    radioTz: string,
    from: moment.Moment,
    to: moment.Moment,
  ): Observable<Slot[]> {
    const offset = moment().tz(radioTz).utcOffset();
    return this.httpClient
      .get<PlanningApi>(
        planningUrls.getPlanning(
          radioId,
          from.clone().subtract(1, 'day').format('YYYY-MM-DD'),
          to.clone().add(1, 'day').format('YYYY-MM-DD'),
        ),
      )
      .pipe(map(data => data.data.map(d => convertToSlot(d, offset))));
  }

  addSlotToPlanning(radioId: number, slot: Slot): Observable<any> {
    return this.httpClient.post<AddedSlotApi>(
      planningUrls.addToPlanning(radioId),
      convertToAddedSlotApi(slot),
    );
  }

  dropSlotToPlanning(radioId: number, slot: Partial<Slot>): Observable<any> {
    return this.httpClient.post<AddedSlotApi>(
      planningUrls.addToPlanning(radioId),
      convertToDroppedSlotApi(slot),
    );
  }

  editSlot(radioId: number, slot: Slot): Observable<any> {
    return this.httpClient.put<AddedSlotApi>(
      planningUrls.editSlot(radioId, slot.id),
      convertToAddedSlotApi(slot),
    );
  }

  moveSlot(radioId: number, slot: Slot): Observable<any> {
    return this.httpClient.put<AddedSlotApi>(
      planningUrls.editSlot(radioId, slot.id),
      convertSlotToPlanningDatesApi(slot),
    );
  }

  moveOccurrence(
    radioId: number,
    slot: Slot,
    occurrence: SlotOccurrence,
  ): Observable<any> {
    return this.httpClient.put(
      planningUrls.moveOccurrence(radioId, slot.id, occurrence.id),
      convertOccurrenceToPlanningDatesApi(occurrence),
    );
  }

  deleteSlotFromPlanning(
    radioId: number,
    slotId: number,
    occurrenceId?: number,
  ): Observable<any> {
    return this.httpClient.delete(
      occurrenceId
        ? planningUrls.deleteOccurrenceFromPlanning(radioId, slotId, occurrenceId)
        : planningUrls.deleteSlotFromPlanning(radioId, slotId),
    );
  }
}
